import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Privacy Policy</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last updated: December 19, 2024</p>

                <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">1. Introduction</h2>
                        <p>
                            Welcome to Eventador ("we," "our," or "us"). We are committed to protecting your personal data
                            and respecting your privacy in accordance with the General Data Protection Regulation (GDPR)
                            and other applicable data protection laws. This Privacy Policy explains how we collect, use,
                            disclose, and safeguard your information when you visit eventador.org and use our services.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2. Data Controller</h2>
                        <p>
                            Eventador, operated by Tridenforge, is the data controller responsible for your personal data.
                            For any questions regarding this policy or your data, contact us at: privacy@eventador.org
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3. Legal Basis for Processing (GDPR Article 6)</h2>
                        <p>We process your personal data based on the following legal grounds:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>Contract Performance:</strong> Processing necessary to fulfill our booking services</li>
                            <li><strong>Legitimate Interests:</strong> Improving our services and preventing fraud</li>
                            <li><strong>Consent:</strong> When you explicitly agree to specific processing activities</li>
                            <li><strong>Legal Obligation:</strong> When required by law or regulatory requirements</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4. Personal Data We Collect</h2>
                        <p><strong>Data you provide directly:</strong></p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Account information (name, email address)</li>
                            <li>Profile information (phone number)</li>
                            <li>Booking and transaction data</li>
                            <li>Communications you send to us</li>
                        </ul>
                        <p className="mt-3"><strong>Data collected automatically:</strong></p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Device and browser information</li>
                            <li>IP address and approximate location</li>
                            <li>Usage data and analytics</li>
                            <li>Cookies and similar technologies</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5. How We Use Your Data</h2>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Provide and maintain our booking services</li>
                            <li>Process transactions and send confirmations</li>
                            <li>Send service-related communications</li>
                            <li>Respond to your inquiries and support requests</li>
                            <li>Detect and prevent fraudulent activity</li>
                            <li>Improve and personalize your experience</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">6. Data Sharing & Third Parties</h2>
                        <p>We may share your data with:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>Event Organizers:</strong> To facilitate your bookings</li>
                            <li><strong>Payment Processors:</strong> To process transactions securely</li>
                            <li><strong>Authentication Providers:</strong> Google (for OAuth sign-in)</li>
                            <li><strong>Cloud Services:</strong> Supabase (database and authentication)</li>
                            <li><strong>Legal Authorities:</strong> When required by law</li>
                        </ul>
                        <p className="mt-3">We ensure all third parties comply with GDPR requirements through appropriate data processing agreements.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">7. International Data Transfers</h2>
                        <p>
                            Your data may be transferred to and processed in countries outside the European Economic Area (EEA).
                            When this occurs, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses
                            (SCCs) approved by the European Commission, to protect your data.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">8. Data Retention</h2>
                        <p>
                            We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy,
                            unless a longer retention period is required by law. Booking records are typically retained for 7 years
                            for legal and accounting purposes. You may request deletion of your account data at any time.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">9. Your Rights Under GDPR</h2>
                        <p>As an EU resident, you have the following rights:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                            <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                            <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                            <li><strong>Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
                            <li><strong>Right to Object:</strong> Object to certain types of processing</li>
                            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
                            <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
                        </ul>
                        <p className="mt-3">To exercise these rights, contact us at: privacy@eventador.org</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">10. Cookies</h2>
                        <p>
                            We use essential cookies for authentication and site functionality. We also use analytics cookies
                            to understand how visitors interact with our site. You can manage cookie preferences through your
                            browser settings. Essential cookies cannot be disabled as they are necessary for the site to function.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">11. Data Security</h2>
                        <p>
                            We implement appropriate technical and organizational measures to protect your personal data, including
                            encryption, secure servers, and access controls. However, no method of transmission over the Internet
                            is 100% secure. In the event of a data breach affecting your rights, we will notify you and the
                            relevant supervisory authority within 72 hours as required by GDPR.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">12. Children's Privacy</h2>
                        <p>
                            Our services are not directed to individuals under 16 years of age. We do not knowingly collect
                            personal data from children. If we become aware that we have collected data from a child, we will
                            take steps to delete it promptly.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">13. Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy periodically. Material changes will be communicated via email
                            or a prominent notice on our website. We encourage you to review this policy regularly.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">14. Contact Us</h2>
                        <p>For questions about this Privacy Policy or to exercise your data rights:</p>
                        <p className="mt-2">
                            <strong>Email:</strong> info@tridenforge.com<br />
                            <strong>Website:</strong> eventador.org<br />
                            <strong>Data Protection Inquiries:</strong> info@tridenforge.com
                        </p>
                    </section>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                    <Link
                        to="/"
                        className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
}
